import { defineMessages } from "react-intl";

import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import FieldType from "@mapmycustomers/shared/enum/fieldModel/FieldType";

import { formatRawDate } from "@app/util/formatters";
import { parseApiDateWithUtcTz } from "@app/util/parsers";

import CommonFieldName from "./CommonFieldName";
import FieldModel from "./impl/FieldModel";
import { createField } from "./impl/fieldModelUtil";
import { meterDistanceValueFormatter } from "./impl/fieldUtil";
import UserField from "./impl/UserField";

export enum DistanceFieldName {
  USER = CommonFieldName.USER,
  DATE = "date",
  TOTAL_DISTANCE = "totalDistance",
}

// `date` arrives as a zone-less "YYYY-MM-DD" string, which the stock dateFormatter leaves blank
// because it is not a Date. Reading it as UTC is what keeps the day: parseApiDate lands on UTC
// midnight and then formats locally, which renders 2026-08-10 as Aug 9 in America/New_York.
const dateOnlyFormatter = (entity: unknown, value: unknown) =>
  typeof value === "string"
    ? formatRawDate(`${value.slice(0, 10)}T00:00:00Z`, "PP", "", parseApiDateWithUtcTz)
    : "";

const displayNames = defineMessages({
  date: {
    id: "distance.field.date",
    defaultMessage: "Date",
    description: "Date field on the location distance breakdown",
  },
  name: {
    id: "distance.field.name",
    defaultMessage: "Name",
    description: "User name field on the location distance breakdown",
  },
  totalDistance: {
    id: "distance.field.totalDistance",
    defaultMessage: "Distance",
    description: "Total distance metric on the location distance breakdown",
  },
});

// A metric row rather than a CRM record, like LeaderboardFieldModel: one row per user per day.
// Only totalDistance is exposed: it is the column the card aggregates, and without Mileage Tracker
// it already equals the location-derived distance, so the split would just repeat it.
const distanceFieldModel = new FieldModel([
  // Rows carry only userId, so the saga attaches the user before the grid renders it.
  new UserField({
    customGridProperties: {
      cellRenderer: "userChipCellRenderer",
      tooltipValueGetter: undefined,
    },
    displayName: displayNames.name,
    displayOrder: 10,
    features: [
      FieldFeature.ALWAYS_VISIBLE,
      FieldFeature.LOCKED,
      FieldFeature.OWNER_FIELD,
      FieldFeature.SORTABLE,
      FieldFeature.VISIBLE_BY_DEFAULT,
    ],
    name: DistanceFieldName.USER,
    platformFilterNames: ["userId"],
    sortName: "userId",
  }),
  ...[
    {
      displayName: displayNames.date,
      features: [FieldFeature.SORTABLE, FieldFeature.VISIBLE_BY_DEFAULT],
      name: DistanceFieldName.DATE,
      type: FieldType.DATE,
      valueFormatter: dateOnlyFormatter,
    },
    // Distances are stored in meters, so this needs the same formatter the trip grid uses or the
    // grid prints a bare five-digit number where the card says "15 km".
    {
      displayName: displayNames.totalDistance,
      features: [FieldFeature.METRIC, FieldFeature.SORTABLE, FieldFeature.VISIBLE_BY_DEFAULT],
      name: DistanceFieldName.TOTAL_DISTANCE,
      type: FieldType.NUMBER,
      valueFormatter: meterDistanceValueFormatter,
    },
  ].map(createField),
]);

export default distanceFieldModel;
